/**
 * Typed refusals for actions blocked because a recall has reached a terminal state.
 *
 * Replaces the previous pattern of throwing a bare Error and re-matching it with
 * `errorMessage.includes('confirmed')` at each call site — which had drifted into four
 * copies with three different wordings, one of which swallowed the error entirely.
 *
 * Two constraints from `errorTranslator` shaped this file:
 *
 * 1. `isSystemError()` treats ANY error carrying a string `code` property as a system
 *    error and collapses it to "A system error occurred." So the discriminator here is
 *    `refusalCode`, never `code`.
 *
 * 2. It also matches on message text containing `network`, `timeout`, `connection`,
 *    `database`, `constraint`, `violates`, `pg_` or `pgrst`. User-facing copy must avoid
 *    all of those — which it should anyway.
 *
 * Both are covered by tests in `__tests__/lifecycleRefusal.test.ts`.
 */

import { isSystemError, translateSystemError } from '@/lib/utils/errorTranslator';

export type LifecycleRefusalCode =
  /** The work was completed. Removing or re-assigning would destroy that record. */
  | 'recall_completed'
  /** The recall was permanently closed out (sold, declined, condition, done elsewhere). */
  | 'recall_cancelled'
  /** The route is confirmed; recalls cannot be removed from it. */
  | 'recall_confirmed'
  /** We could not determine the recall's state, so we refused rather than guess. */
  | 'status_check_failed';

/** What we know about the completion, so the message can say who and when. */
export interface LifecycleRefusalEvidence {
  completedAt?: string;
  completedBy?: string;
  finalStatus?: string;
}

export interface LifecycleRefusalCopy {
  /** Short title, safe to render in a narrow inline slot. */
  title: string;
  /** One sentence, plain language, no jargon. */
  description: string;
}

const STALE_LIST_HINT = 'Your list may be out of date — refresh to see the current state.';

/** e.g. "31 Aug 2026". Returns undefined for anything unparseable, never "Invalid Date". */
function formatDay(iso?: string): string | undefined {
  if (!iso) return undefined;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * "Completed on 31 Aug 2026 by Geston Dessalines." Degrades cleanly when we know less,
 * so no caller has to branch on how much evidence we happen to have.
 */
function describeCompletion(evidence: LifecycleRefusalEvidence): string {
  const day = formatDay(evidence.completedAt);
  const who = evidence.completedBy?.trim();

  if (day && who) return `Completed on ${day} by ${who}.`;
  if (day) return `Completed on ${day}.`;
  if (who) return `Completed by ${who}.`;
  return 'This recall has already been completed.';
}

type CopyBuilder = (evidence: LifecycleRefusalEvidence) => LifecycleRefusalCopy;

const REFUSAL_COPY: Record<LifecycleRefusalCode, CopyBuilder> = {
  recall_completed: evidence => ({
    title: 'Already completed',
    description: `${describeCompletion(evidence)} ${STALE_LIST_HINT}`
  }),

  recall_cancelled: () => ({
    title: 'Already closed',
    description: `This recall was closed and is no longer open work. ${STALE_LIST_HINT}`
  }),

  recall_confirmed: () => ({
    title: 'Route is confirmed',
    description: 'Recalls cannot be removed once their route has been confirmed.'
  }),

  status_check_failed: () => ({
    title: "Couldn't check this recall",
    description:
      "We couldn't confirm whether this recall is already finished, so nothing was " +
      'changed. Please try again.'
  })
};

export function buildRefusalCopy(
  refusalCode: LifecycleRefusalCode,
  evidence: LifecycleRefusalEvidence = {}
): LifecycleRefusalCopy {
  return REFUSAL_COPY[refusalCode](evidence);
}

/**
 * A refusal is not a failure — it is the system correctly declining a destructive action.
 * Render `title` and `description`; `message` exists for logs.
 */
export class LifecycleRefusalError extends Error {
  readonly refusalCode: LifecycleRefusalCode;
  readonly title: string;
  readonly description: string;
  readonly evidence: LifecycleRefusalEvidence;

  constructor(refusalCode: LifecycleRefusalCode, evidence: LifecycleRefusalEvidence = {}) {
    const copy = buildRefusalCopy(refusalCode, evidence);
    super(`${copy.title}: ${copy.description}`);
    this.name = 'LifecycleRefusalError';
    this.refusalCode = refusalCode;
    this.title = copy.title;
    this.description = copy.description;
    this.evidence = evidence;

    // Preserve the prototype chain under ES5 downlevel output.
    Object.setPrototypeOf(this, LifecycleRefusalError.prototype);
  }
}

export function isLifecycleRefusal(error: unknown): error is LifecycleRefusalError {
  return error instanceof LifecycleRefusalError;
}

/**
 * Maps a blocking `assignment_status` to the refusal that explains it.
 * `not_there` is legacy and means the same as a close-out.
 * Returns null for statuses that may be removed.
 */
const REFUSAL_BY_STATUS: Record<string, LifecycleRefusalCode> = {
  completed: 'recall_completed',
  cancelled: 'recall_cancelled',
  not_there: 'recall_cancelled',
  confirmed: 'recall_confirmed'
};

export function refusalForAssignmentStatus(
  status: string,
  evidence: LifecycleRefusalEvidence = {}
): LifecycleRefusalError | null {
  const refusalCode = REFUSAL_BY_STATUS[status];
  return refusalCode ? new LifecycleRefusalError(refusalCode, evidence) : null;
}

const GENERIC_FAILURE_TITLE = 'Something went wrong';

/**
 * Turns any thrown error into copy fit to show a user.
 *
 * The single place that decides how a failed action reads, so the pages that remove
 * recalls stop each re-deriving it — previously via `errorMessage.includes('confirmed')`,
 * with three different wordings between them and one branch that showed nothing at all.
 */
export function describeActionError(error: unknown, context: string): LifecycleRefusalCopy {
  if (isLifecycleRefusal(error)) {
    return { title: error.title, description: error.description };
  }

  if (isSystemError(error)) {
    return {
      title: GENERIC_FAILURE_TITLE,
      description: translateSystemError(error, context).message
    };
  }

  const message = error instanceof Error ? error.message : '';
  return {
    title: GENERIC_FAILURE_TITLE,
    description: message || `We couldn't finish ${context}. Please try again.`
  };
}
